// import NormalizeWheel from 'normalize-wheel'
import { gsap } from "gsap";
import morphdom from 'morphdom';

import Modal from './components/Modal'
import Navigation from './components/Navigation'
import Analytics from './components/Analytics'
import Livewire from './components/Livewire'
import ResponsiveImages from './components/ResponsiveImages'
import BalanceText from './components/BalanceText'
import NewsletterForm from './components/NewsletterForm'

/* Pages based on views/templates */
import Home from './pages/Home'
import Default from './pages/Default'
import Contact from './pages/Contact'
import Journal from './pages/Journal'
import Projects from './pages/Projects'

import { get } from './utils/ajax'
import { updateHeadMeta } from './utils/updateHeadMeta'
import { setFocusRing } from './utils/setFocusRing'
import { setDevice } from './utils/setDevice'
import { setCurrentTime } from './utils/setCurrentTime'
import { setCurrentTemp } from './utils/setCurrentTemp'
import { getAddress, getCurrentUrl, isVisible } from './helpers';

class App {
  constructor () {
    this.content = document.querySelector('#main')
    this.menuEl  = document.querySelector('.c-menu-overlay [data-scrollable]')
    this.slug = this.content.dataset.slug
    this.eventsEnabled = true
    this.siteInitialised = false

    this.template = document.querySelector('[data-template]').getAttribute('data-template')

    this.scrollHistory = {
      positions: [],
      lastElementClicked: '',
      scrollToSavedPosition: false
    }

    // history.scrollRestoration is set in critical.js, synchronously,
    // before this (deferred) script runs — see the comment there for why
    // it can't live here.

    this.currentTime = new setCurrentTime()
    this.currentTemp = new setCurrentTemp()

    this.keycode = []

    setFocusRing()
    setDevice()

    this.createComponents()
    this.createPages()

    this.addEventListeners()
    this.initStatamicLivePreview()

    document.documentElement.classList.remove('js-loading');
    document.documentElement.classList.add('js-loaded');

  }

  createComponents () {
    this.navigationPrimary = new Navigation({
      classes: {
        linksActive: 'c-nav-primary__item--active'
      },
      element: '.c-nav-primary',
      elements: {
        items: '.c-nav-primary__item',
        links: '.c-nav-primary__item a',
      }
    })

    this.navigationSecondary = new Navigation({
      classes: {
        linksActive: 'c-nav-secondary__item--active'
      },
      element: '.c-nav-secondary',
      elements: {
        items: '.c-nav-secondary__item',
        links: '.c-nav-secondary__item a',
      }
    })

    this.menu = new Modal({
      template: this.template,
      target: 'modal'
    })

    this.analytics = new Analytics()
    this.livewire = new Livewire()
    this.responsiveImages = new ResponsiveImages()
    this.balanceText = new BalanceText()
    this.newsletterForm = new NewsletterForm()
  }

  createPages () {
    this.content = document.querySelector('#main')
    this.slug = this.content.dataset.slug || window.location.pathname

    this.pages = new Map()

    this.pages.set('/', new Home())
    this.pages.set('/default', new Default())
    // this.pages.set('about', new About())
    this.pages.set('/contact', new Contact())
    this.pages.set('/journal', new Journal())
    this.pages.set('/projects', new Projects())
    // this.pages.set('/projects/*', new Projects())


    if (this.slug.indexOf('/projects') > -1 ) {
      this.page = this.pages.get('/projects')
    } else if (this.slug.indexOf('/journal') > -1 ) {
      this.page = this.pages.get('/journal')
    } else {
      this.page = this.pages.get(this.slug) || this.pages.get('/default')
    }

    this.page.create(false)
    this.page.show(0, true)


    console.log('this.page.show;', this.page.show)
    console.log('this.content;', this.content)
    console.log('this.slug;', this.slug)
    console.log('this.page;', this.page)

    // if (this.navigation) {
    //   this.navigation.show(this.slug)
    // }

    this.addLinkListeners()

    /*
     * Header reveal on initial site load
     *
     */
    if (!this.siteInitialised) {
      this.tl = new gsap.timeline()
      this.headerContainer = document.querySelector('.c-header__nav')
      this.headerMiddleNav = document.querySelector('.c-nav-secondary')
      this.headerNavs = document.querySelector('.c-header').querySelectorAll('[data-reveal]')

      if ( isVisible(this.headerMiddleNav)) {
          this.tl
          .fromTo(this.headerContainer, 1.5, {
            yPercent: -100,
          },
          {
            yPercent: 0,
            autoAlpha: 1,
            ease: "power2.Out",
          })
          .fromTo(this.headerNavs, 1.5, {
            yPercent: -100,
            autoAlpha: 0,
          },
          {
            yPercent: 0,
            autoAlpha: 1,
            stagger: .5,
            ease: "power2.Out",
          }, 0)
      } else {
          this.tl
          .fromTo(this.headerContainer, 1, {
            yPercent: -100,
            autoAlpha: 0,
          },
          {
            yPercent: 0,
            autoAlpha: 1,
            ease: "power2.Out",
          })
      }
    }

    this.siteInitialised = true

  }  

  /**
   * Events.
   */
  // onLoaded () {

  //  console.log('this.page', this.page)
  //   this.onResize()

  //   this.page.show()
  // }

  onPopState (event) {

    // Livewire's own query-string-bound properties (filter category,
    // pagination) push their own real history entries — category="push",
    // confirmed in both journal's and projects' wire:effects output. Those
    // are same-page state changes, not navigations: popstate is a single
    // global event, so our listener fires for them exactly as it would for
    // a real navigation, unless we tell the two apart.
    //
    // Our own pushState calls (in onRequest, below) stamp {spa: true, url}
    // into the state object. If that marker is present, this is definitely
    // ours. If it's absent, it's either the original hard-loaded entry
    // (state is null — a real "page" we still need to fetch) or one of
    // Livewire's — distinguished by whether the *pathname* actually
    // changed. Livewire's pushes only ever add/change a query string on
    // the current page; a real navigation changes the path.
    const isOwnNavigation = !!(event && event.state && event.state.spa === true)
    const previousPathname = this.url ? new URL(this.url).pathname : null
    const pathnameChanged = previousPathname !== window.location.pathname

    if (!isOwnNavigation && !pathnameChanged) {
      // Livewire's own state change — let its own reactivity handle the
      // re-render in place. Don't touch scroll history or run our
      // fetch-and-swap cycle for it.
      return
    }

    this.scrollHistory.scrollToSavedPosition = true

    /*
     * Save the scroll position of the page currently on screen — that's
     * `this.url` (set by the last onChange call), not `this.urlPrevious`
     * (which is one hop further back). The DOM hasn't been swapped yet at
     * this point, only window.location has, so window.scrollY still
     * reflects the outgoing page even though `this.url` is about to change.
     */

    const itemCount = this.page && this.page.getLoadedItemCount ? this.page.getLoadedItemCount() : undefined

    let item = this.scrollHistory.positions.find(x => x.id == this.url)
    if (item) {
      item.position = window.scrollY
      item.itemCount = itemCount
    } else if (this.url) {
      this.scrollHistory.positions.push({id: this.url, position: window.scrollY, itemCount})
    }

    /*
     * load the page
     */

    this.onChange({
      url: window.location.pathname,
      push: false,
    })


    if (this.page && this.page.onPopState) {
      this.page.onPopState()
    }
  }

  onChange ({ callback = () => {}, push = true, url = null, hash = undefined }) {
    // Normalise to a full URL up front — `url` arrives absolute from onClick
    // (link.href) but path-only from onPopState (window.location.pathname).
    // Comparing/storing a mix of the two is what corrupted urlPrevious.
    const normalizedUrl = new URL(url, window.location.origin).href

    if (this.isLoading || this.url === normalizedUrl) {
      return
    }

    this.urlPrevious = this.url || ''
    this.url = normalizedUrl

    this.isLoading = true

    // the hash (if any) is passed in explicitly by the caller now, rather
    // than read off a stale/global `event` reference.
    this.scrollTo = { hash }

    if (this.responsiveImages) {
      this.responsiveImages.cleanup()
    }

    if (this.balanceText) {
      this.balanceText.cleanup()
    }    

    if (this.livewire) {
      this.livewire.cacheLivewireComponents()
    }

    if (this.menu) {
      this.menu.hide()
    }

    this.page.animateOut()
    this.currentTime.clear()

    get(url).then(response => {
      this.onRequest({
        callback,
        push,
        response,
        url
      })
    }).catch(response => {
      this.onRequest({
        callback,
        push,
        response,
        url: '/'
      })
    })
  }

  async onRequest ({ callback, push, response, url }) {
    
    if (this.cursor) {
      this.cursor.onNavigationStart()
    }

    if (this.navigationPrimary) {
      this.navigationPrimary.onNavigationStart()
    }

    if (this.navigationSecondary) {
      this.navigationSecondary.onNavigationStart()
    }

    this.eventsEnabled = true

    const html = document.createElement('div')

    html.innerHTML = response

    const content = html.querySelector('#main')
    const menu = html.querySelector('.c-menu-overlay [data-scrollable]')

    if (this.page) {
      await this.page.hide()
    }

    if (this.livewire) {
      this.livewire.destroyLivewireComponents(this.content)
    }

    this.content.innerHTML = content.innerHTML
    this.content.dataset.slug = content.dataset.slug
    this.content.dataset.template = content.dataset.template
    document.body.dataset.template = content.dataset.template


    console.log(this.menuEl, menu)
    
    morphdom(this.menuEl, menu)

    const slug = content.dataset.slug
    const page = this.pages.get(slug) || this.pages.get('/default')

    page.create()

    this.calcScrollToPos()

    this.slug = slug

    this.page = page
    // this.page.setScrollPos(this.scrollTo, this.scrollHistory)
    this.page.show(this.scrollHistory)


    if (this.responsiveImages) {
      this.responsiveImages.create()
    }

    if (this.balanceText) {
      this.balanceText.create()
    }    

    if (this.newsletterForm) {
      this.newsletterForm.create()
    }

    if (this.livewire) {
      this.livewire.refreshLivewireComponents()
    }

    if (this.navigationPrimary) {
      this.navigationPrimary.onNavigationEnd(this.slug)
    }

    if (this.navigationSecondary) {
      this.navigationSecondary.onNavigationEnd(this.slug)
    }


    updateHeadMeta(html)

    if (push) {
      // { spa: true, url } marks this as one of our own navigations, so
      // onPopState can tell it apart from a history entry Livewire pushed
      // on its own (filter/pagination query-string changes) — see the
      // comment there. `url` is this.url (already normalised to a full,
      // canonical URL in onChange) rather than the raw param, which can
      // arrive path-only from a popstate-triggered call.
      window.history.pushState({ spa: true, url: this.url }, document.title, url)
    }

    callback()

    this.currentTime.start()
    this.analytics.update()
    this.addLinkListeners()

    // Only reopen the navigation guard once this navigation has fully
    // landed — DOM swapped, scroll positioned, page reassigned. Clearing
    // this at the top of the function (before the `await this.page.hide()`
    // that precedes all of that) let a fast second popstate/click start a
    // second, overlapping onRequest before the first had finished
    // positioning scroll, so whichever one resolved last would win — the
    // unreliable restoration this was meant to fix.
    this.isLoading = false

  }

  onTouchDown (event) {
    if (!this.eventsEnabled) return

    if (this.page && this.page.onTouchDown) {
      this.page.onTouchDown(event)
    }

    if (this.menu && this.menu.onTouchDown) {
      this.menu.onTouchDown(event)
    }

  }

  onTouchMove (event) {
    if (!this.eventsEnabled) return

    if (this.page && this.page.onTouchDown) {
      this.page.onTouchMove(event)
    }
  }

  onTouchUp (event) {
    if (!this.eventsEnabled) return

    if (this.page && this.page.onTouchDown) {
      this.page.onTouchUp(event)
    }
  }

  onKeyDown (event) {
    if (!this.eventsEnabled) return
    //* Command Key Pressed *//
    event.ctrlKey == true ? this.keycode.ctrl = 'active' : this.keycode.ctrl = ''
    event.metaKey == true ? this.keycode.meta = 'active' : this.keycode.meta = ''
    event.altKey == true ? this.keycode.alt = 'active' : this.keycode.alt = ''
    // console.log('event.key', event.key, this.keycode)

    if (this.page && this.page.onKeyDown) {
      this.page.onKeyDown(event)
    }
    
    if (this.menuModal && this.menuModal.onKeyDown) {
      this.menuModal.onKeyDown(event)
    }

  }

  onKeyUp (event) {
    /* reset the keycode state */
    this.keycode = []
  }

  onBlur (event) {
    /* reset the keycode state */
    this.keycode = []
  }    

  onWheel (event) {
    // const normalizedWheel = NormalizeWheel(event)

    // if (this.page && this.page.onWheel) {
    //   this.page.onWheel(normalizedWheel)
    // }
  }

  onResize () {
    if (this.page && this.page.onResize) {
      this.page.onResize()
    }

    // window.requestAnimationFrame(() => {
    //   if (this.canvas && this.canvas.onResize) {
    //     this.canvas.onResize()
    //   }
    // })
  }

  onLivewireUpdated () {
    this.addLinkListeners()
  }


  /***
   * Calculations
   */
   calcScrollToPos (timing = 0) {
    // if a scrollTo hash exists from the onChange,
    // store the values and scroll to the hash,
    // otherwise, reset the scrollTo position on page change
    // to the top of the page.
    if (this.scrollTo.hash) {
      this.scrollTo = {
        hash: this.scrollTo.hash,
        target: document.querySelector(`${this.scrollTo.hash}`)
      }
      if (this.scrollTo.target)  {
        this.scrollTo.targetHeight = this.scrollTo.target.offsetHeight;
        this.scrollTo.targetMargin = parseInt(`${window.getComputedStyle(this.scrollTo.target).marginTop}`, 10); 
        this.scrollTo.offsetY = ((window.innerHeight - this.scrollTo.targetHeight) / 2) * 1;
        this.scrollTo.y = this.scrollTo.hash

        // if targetHeight > window.innerHeight; offset marginTop of target instead
        if (this.scrollTo.targetHeight > window.innerHeight) {
          this.scrollTo.offsetY = this.scrollTo.targetMargin;
        }
      }
    } else {
      this.scrollTo.y = 0
      this.scrollTo.offsetY = 0
    }

    // if (samePageWithHash) {
      this.page.setScrollPos(this.scrollTo, this.scrollHistory, timing)
    // }
   }

  /***
   * Listeners.
   */
  addEventListeners () {
    window.addEventListener('popstate', this.onPopState.bind(this))
    // window.addEventListener('mousewheel', this.onWheel.bind(this))

    window.addEventListener('mousedown', this.onTouchDown.bind(this))
    window.addEventListener('mousemove', this.onTouchMove.bind(this))
    window.addEventListener('mouseup', this.onTouchUp.bind(this))

    // window.addEventListener('touchstart', this.onTouchDown.bind(this))
    // window.addEventListener('touchmove', this.onTouchMove.bind(this))
    // window.addEventListener('touchend', this.onTouchUp.bind(this))

    window.addEventListener('keydown', this.onKeyDown.bind(this))
    window.addEventListener('keyup', this.onKeyUp.bind(this))
    window.addEventListener('blur', this.onBlur.bind(this))

    window.addEventListener('resize', this.onResize.bind(this))

    window.addEventListener('updated', this.onLivewireUpdated.bind(this))

    /* livewire pagination */
    window.addEventListener('projects-paginated', this.addLinkListeners.bind(this))

    window.addEventListener('articles-paginated', this.addLinkListeners.bind(this))

    


  }

  /**
   * Statamic Live Preview.
   *
   * With `refresh: false` on the collections' preview_targets (see
   * content/collections/*.yaml), Statamic stops fully reloading the CP
   * iframe on every edit and instead posts a `statamic.preview.updated`
   * message. Refetch the entry's rendered HTML and morph it into #main in
   * place, rather than swapping innerHTML wholesale — that's what caused
   * the white flash/reload on every keystroke.
   *
   * Morphdom is scoped to #main's children only (not #main itself) so it
   * never touches the inline style GSAP sets on #main for the page-reveal
   * animation (autoAlpha) — diffing that in would strip it and re-trigger
   * the fade-in on every edit.
   */
  initStatamicLivePreview () {
    let inflight = null

    window.addEventListener('message', async (event) => {
      if (event.data?.name !== 'statamic.preview.updated') return

      const url = event.data.url || window.location.href

      if (inflight) {
        inflight.abort()
        inflight = null
      }
      const controller = new AbortController()
      inflight = controller

      try {
        const response = await fetch(url, { signal: controller.signal })
        if (!response.ok) return

        const html = await response.text()
        inflight = null

        const newDoc = new DOMParser().parseFromString(html, 'text/html')
        const newMain = newDoc.querySelector('#main')
        if (!this.content || !newMain) return

        morphdom(this.content, newMain, { childrenOnly: true })

        // #main carries the page's GSAP-driven reveal (autoAlpha, set by
        // Page.show()'s timeline). childrenOnly keeps morphdom off #main's
        // own attributes, but the page-reveal timeline can still be
        // mid-flight (or get retriggered) around a live-preview edit, and
        // if it's caught between its 0 start-state and the 1 it tweens to,
        // #main is left showing whatever partial opacity it was at —
        // which reads as the preview going blank. Kill any tween still
        // targeting #main and pin it visible so an edit never depends on
        // that timeline's timing.
        gsap.killTweensOf(this.content)
        gsap.set(this.content, { autoAlpha: 1 })

        this.refreshLivePreviewEnhancements()
      } catch (err) {
        if (err.name !== 'AbortError') console.error('[live-preview]', err)
      }
    })
  }

  /**
   * Re-run the (non-animation) JS-driven DOM enhancements a live-preview
   * morph can invalidate — responsive image sources, balanced headings.
   * This is the one place that owns "what needs to happen after #main's
   * content changes underneath us"; add any future one here rather than
   * back in the message handler.
   *
   * The word-splitting reveal animations (Text/Paragraph) don't need
   * (and specifically must not get) this treatment: Page.createAnimations()
   * skips them entirely while html.is-live-preview is set, so content
   * renders in its final, static state and there's nothing for a morph
   * to invalidate or replay in the first place — see the comment there.
   */
  refreshLivePreviewEnhancements () {
    if (this.responsiveImages) {
      this.responsiveImages.cleanup()
      this.responsiveImages.create()
    }

    if (this.balanceText) {
      this.balanceText.cleanup()
      this.balanceText.create()
    }
  }

  addLinkListeners () {
    const links = document.querySelectorAll('a')

    links.forEach(link => {
      const isLocal = link.href.indexOf(window.location.origin) > -1
      const opensNewTab = link.target === '_blank'

      const isNotEmail = link.href.indexOf('mailto') === -1
      const isNotPhone = link.href.indexOf('tel') === -1


      if (isLocal && !opensNewTab) {
        link.onclick = event => {
          this.onClick(link, event)
        }
      } else if (isNotEmail && isNotPhone) {
        link.rel = 'noopener'
        link.target = '_blank'
      }
    })

  }

  onClick (link, event) {

    if (this.keycode.ctrl == 'active' ||
        this.keycode.meta == 'active' ||
        this.keycode.alt == 'active')
    {
      window.open(link.href, "_blank");
      return
    }

    const itemCount = this.page && this.page.getLoadedItemCount ? this.page.getLoadedItemCount() : undefined

    let item = this.scrollHistory.positions.find(x => x.id == window.location.href)
    if (item) {
      item.position = window.scrollY
      item.itemCount = itemCount
    } else {
      this.scrollHistory.positions.push({id: window.location.href, position: window.scrollY, itemCount})
    }

    // Use `link` (the anchor actually bound to this handler), not
    // `event.target` — a click can land on a child element (e.g. a <span>
    // inside the <a>), which has no .pathname/.hash/.href of its own.
    this.scrollHistory.lastElementClicked = link
    this.scrollHistory.scrollToSavedPosition = false

    const samePage = getAddress(link) == getCurrentUrl() || getAddress(link) == '' ? true : false
    const isHash = link.hash ? true : false
    const isDownload = link.hasAttribute('download')

    if (isDownload) {
      return
    }

    event.preventDefault()

    if (isHash) {
      // save the hash from the current event for the onRequest
      this.scrollTo = { hash: link.hash }
    }

    if (samePage) {
      if (isHash) {
        this.calcScrollToPos(1)
      }
      if (this.menu) {
        this.menu.hide()
      }
      return
    }

    this.onChange({
      url: link.href,
      hash: isHash ? link.hash : undefined
    })

    this.eventsEnabled = false

  }

}

new App()
